import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { inr, formatDate, csvRow } from "@/lib/utils2";
import { Download, Pencil, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

/**
 * Excel-like spreadsheet view: every truck release with full Material AND Transport columns,
 * including both PURCHASE (cost paid) and BILL (charged to party) sides.
 */
export default function AccountsSheet() {
  const [trucks, setTrucks] = useState([]);
  const [parties, setParties] = useState([]);
  const [transporters, setTransporters] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [filter, setFilter] = useState({ party_id: "all", transporter_id: "all", from: "", to: "" });

  const reload = async () => {
    const [t, p, tr, m] = await Promise.all([
      api.get("/trucks"), api.get("/parties"), api.get("/transporters"), api.get("/materials"),
    ]);
    setTrucks(t.data); setParties(p.data); setTransporters(tr.data); setMaterials(m.data);
  };
  useEffect(() => { reload(); }, []);

  const removeTruck = async (t) => {
    if (t.material_bill_id || t.transport_bill_id || t.combined_bill_id) {
      return toast.error("This truck is already on a bill — delete that bill first.");
    }
    if (!confirm(`Delete entry for ${t.vehicle_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/trucks/${t.id}`);
      toast.success("Entry deleted");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const pname = (id) => parties.find((p) => p.id === id)?.name || "—";
  const tinfo = (id) => transporters.find((p) => p.id === id);
  const tname = (id) => tinfo(id)?.name || "—";
  const tphone = (id) => tinfo(id)?.phone || "";
  const mname = (id) => materials.find((p) => p.id === id)?.name || "";
  const mhsn = (id) => materials.find((p) => p.id === id)?.hsn || "";

  const filtered = useMemo(() => {
    return trucks.filter((t) => {
      if (filter.party_id !== "all" && t.party_id !== filter.party_id) return false;
      if (filter.transporter_id !== "all" && t.transporter_id !== filter.transporter_id) return false;
      if (filter.from && t.entry_date < filter.from) return false;
      if (filter.to && t.entry_date > filter.to) return false;
      return true;
    });
  }, [trucks, filter]);

  const totals = filtered.reduce((a, t) => ({
    matPurchase: a.matPurchase + (t.material_purchase_amount || 0),
    matBill: a.matBill + (t.material_bill_amount || 0),
    trnPurchase: a.trnPurchase + (t.transport_purchase_amount || 0),
    trnBill: a.trnBill + (t.transport_bill_amount || 0),
    combinedBill: a.combinedBill + (t.combined_bill_amount || 0),
    profit: a.profit + (t.profit || 0),
  }), { matPurchase: 0, matBill: 0, trnPurchase: 0, trnBill: 0, combinedBill: 0, profit: 0 });

  const exportCsv = () => {
    const headers = [
      "S.No", "Entry Date", "Vehicle", "Party", "Billing Mode",
      "Transporter Name", "Transporter Phone",
      "Material", "HSN",
      "PO No", "PO Date", "Unloading Date",
      "Mat Wgt", "Mat Pur Rate", "Mat Pur Amt", "Mat Bill Rate", "Mat Bill Amt", "Mat GST%",
      "Mat Bill No",
      "Trn Wgt", "Trn Pur Rate", "Trn Pur Amt (Paid)", "Trn Bill Rate", "Trn Bill Amt", "Trn GST%",
      "Trn Bill No",
      "Combined Rate", "Combined Bill Amt", "Combined GST%", "Combined Bill No",
      "Total Owed to Transporter", "Profit",
    ];
    const rows = filtered.map((t, i) => [
      i + 1, t.entry_date, t.vehicle_number, pname(t.party_id), t.billing_mode || "split",
      tname(t.transporter_id), tphone(t.transporter_id),
      mname(t.material_id), mhsn(t.material_id),
      t.po_number || "", t.po_date || "", t.unloading_date || "",
      t.material_weight || 0, t.material_purchase_rate || 0, t.material_purchase_amount || 0,
      t.material_bill_rate || 0, t.material_bill_amount || 0, t.material_gst_percent || 0,
      t.material_bill_number || "",
      t.transport_weight || 0, t.transport_purchase_rate || 0, t.transport_purchase_amount || 0,
      t.transport_bill_rate || 0, t.transport_bill_amount || 0, t.transport_gst_percent || 0,
      t.transport_bill_number || "",
      t.combined_bill_rate || 0, t.combined_bill_amount || 0, t.combined_gst_percent || 0,
      t.combined_bill_number || "",
      (t.material_purchase_amount || 0) + (t.transport_purchase_amount || 0),
      t.profit || 0,
    ]);
    const csv = [headers, ...rows].map(csvRow).join("");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `tulsi-accounts-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 md:p-10 lg:p-12 max-w-[1900px] mx-auto" data-testid="accounts-sheet-page">
      <PageHeader
        title="Accounts Sheet"
        subtitle="Every truck release with Material PURCHASED + Transportation PAID + Billing — all sides visible in one row."
        action={
          <Button onClick={exportCsv} data-testid="export-csv-btn" className="rounded-sm bg-brand-forest text-white">
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>
        }
      />

      <Card className="rounded-sm border-brand-line shadow-none p-5 mb-5">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 items-end">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-brand-muted">Party</Label>
            <Select value={filter.party_id} onValueChange={(v) => setFilter({ ...filter, party_id: v })}>
              <SelectTrigger className="mt-1.5 h-10 rounded-sm bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Parties</SelectItem>
                {parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-brand-muted">Transporter</Label>
            <Select value={filter.transporter_id} onValueChange={(v) => setFilter({ ...filter, transporter_id: v })}>
              <SelectTrigger className="mt-1.5 h-10 rounded-sm bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Transporters</SelectItem>
                {transporters.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-brand-muted">From</Label>
            <Input type="date" className="mt-1.5 h-10 rounded-sm bg-white" value={filter.from} onChange={(e) => setFilter({ ...filter, from: e.target.value })} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-brand-muted">To</Label>
            <Input type="date" className="mt-1.5 h-10 rounded-sm bg-white" value={filter.to} onChange={(e) => setFilter({ ...filter, to: e.target.value })} />
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.15em] text-brand-muted">Rows</div>
            <div className="mt-1.5 font-serif text-2xl tabular-nums">{filtered.length}</div>
          </div>
        </div>
      </Card>

      <Card className="rounded-sm border-brand-line shadow-none overflow-hidden">
        <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-secondary sticky top-0 z-10 text-[10px] uppercase tracking-[0.1em] text-brand-muted">
              <tr>
                <th rowSpan={2} className="text-left px-3 py-2 border-r border-brand-line">#</th>
                <th rowSpan={2} className="text-left px-2 py-2 border-r border-brand-line">Date</th>
                <th rowSpan={2} className="text-left px-2 py-2 border-r border-brand-line">Vehicle</th>
                <th rowSpan={2} className="text-left px-2 py-2 border-r border-brand-line">Party</th>
                <th rowSpan={2} className="text-left px-2 py-2 border-r border-brand-line">Transporter</th>
                <th colSpan={9} className="text-center px-2 py-2 border-r border-brand-line bg-brand-forest/5 text-brand-forest">Material (Purchase + Bill)</th>
                <th colSpan={7} className="text-center px-2 py-2 border-r border-brand-line bg-brand-sand/10 text-brand-terracotta">Transport (Paid + Bill)</th>
                <th rowSpan={2} className="text-right px-3 py-2">Profit</th>
                <th rowSpan={2} className="px-2 py-2"></th>
              </tr>
              <tr>
                <th className="text-left px-2 py-1.5 bg-brand-forest/5">Material</th>
                <th className="text-left px-2 py-1.5 bg-brand-forest/5">PO</th>
                <th className="text-right px-2 py-1.5 bg-brand-forest/5">Wgt</th>
                <th className="text-right px-2 py-1.5 bg-brand-forest/5">Pur Rate</th>
                <th className="text-right px-2 py-1.5 bg-brand-forest/5">Pur Amt</th>
                <th className="text-right px-2 py-1.5 bg-brand-forest/5">Bill Rate</th>
                <th className="text-right px-2 py-1.5 bg-brand-forest/5">Bill Amt</th>
                <th className="text-right px-2 py-1.5 bg-brand-forest/5">GST</th>
                <th className="text-left px-2 py-1.5 bg-brand-forest/5 border-r border-brand-line">Bill No</th>

                <th className="text-right px-2 py-1.5 bg-brand-sand/10">Wgt</th>
                <th className="text-right px-2 py-1.5 bg-brand-sand/10">Paid Rate</th>
                <th className="text-right px-2 py-1.5 bg-brand-sand/10">Paid Amt</th>
                <th className="text-right px-2 py-1.5 bg-brand-sand/10">Bill Rate</th>
                <th className="text-right px-2 py-1.5 bg-brand-sand/10">Bill Amt</th>
                <th className="text-right px-2 py-1.5 bg-brand-sand/10">GST</th>
                <th className="text-left px-2 py-1.5 bg-brand-sand/10 border-r border-brand-line">Bill No</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={23} className="px-5 py-10 text-center text-brand-muted">No rows match.</td></tr>
              )}
              {filtered.map((t, i) => (
                <tr key={t.id} className="border-t border-brand-line hover:bg-secondary/30" data-testid={`acc-row-${t.id}`}>
                  <td className="px-3 py-2 tabular-nums">{i + 1}</td>
                  <td className="px-2 py-2 whitespace-nowrap">{formatDate(t.entry_date)}</td>
                  <td className="px-2 py-2 font-medium whitespace-nowrap">
                    {t.vehicle_number}
                    {t.billing_mode === "combined" && <span className="ml-1.5 inline-block px-1 py-0.5 rounded-sm bg-brand-sand/40 text-[8px] tracking-wider font-semibold">COMB</span>}
                  </td>
                  <td className="px-2 py-2 max-w-[140px] truncate" title={pname(t.party_id)}>{pname(t.party_id)}</td>
                  <td className="px-2 py-2 max-w-[140px] truncate" title={`${tname(t.transporter_id)}${tphone(t.transporter_id) ? ` · ${tphone(t.transporter_id)}` : ""}`}>
                    <div>{tname(t.transporter_id)}</div>
                    {tphone(t.transporter_id) && <div className="text-[9px] text-brand-muted">{tphone(t.transporter_id)}</div>}
                  </td>
                  {/* Material side */}
                  <td className="px-2 py-2 bg-brand-forest/[0.03] max-w-[120px] truncate">{mname(t.material_id) || "—"}</td>
                  <td className="px-2 py-2 bg-brand-forest/[0.03] font-mono text-[10px]">{t.po_number || "—"}</td>
                  <td className="px-2 py-2 bg-brand-forest/[0.03] text-right tabular-nums">{t.material_weight || 0}</td>
                  <td className="px-2 py-2 bg-brand-forest/[0.03] text-right tabular-nums text-brand-muted">{t.material_purchase_rate || 0}</td>
                  <td className="px-2 py-2 bg-brand-forest/[0.03] text-right tabular-nums text-brand-muted">{inr(t.material_purchase_amount)}</td>
                  <td className="px-2 py-2 bg-brand-forest/[0.03] text-right tabular-nums">{t.material_bill_rate || 0}</td>
                  <td className="px-2 py-2 bg-brand-forest/[0.03] text-right tabular-nums">
                    {t.billing_mode === "combined" ? (
                      <span title={`Combined inclusive: ${inr(t.combined_bill_amount)}`} className="text-brand-muted">incl.</span>
                    ) : inr(t.material_bill_amount)}
                  </td>
                  <td className="px-2 py-2 bg-brand-forest/[0.03] text-right tabular-nums">{t.material_gst_percent || 0}%</td>
                  <td className="px-2 py-2 bg-brand-forest/[0.03] font-mono text-[10px] text-brand-forest">{t.billing_mode === "combined" ? (t.combined_bill_number || "—") : (t.material_bill_number || "—")}</td>
                  {/* Transport side */}
                  <td className="px-2 py-2 bg-brand-sand/[0.05] text-right tabular-nums">{t.transport_weight || 0}</td>
                  <td className="px-2 py-2 bg-brand-sand/[0.05] text-right tabular-nums text-brand-muted">{t.transport_purchase_rate || 0}</td>
                  <td className="px-2 py-2 bg-brand-sand/[0.05] text-right tabular-nums text-brand-muted">{inr(t.transport_purchase_amount)}</td>
                  <td className="px-2 py-2 bg-brand-sand/[0.05] text-right tabular-nums">{t.transport_bill_rate || 0}</td>
                  <td className="px-2 py-2 bg-brand-sand/[0.05] text-right tabular-nums">
                    {t.billing_mode === "combined" ? (
                      <span className="font-semibold text-brand-ink">{inr(t.combined_bill_amount)}</span>
                    ) : inr(t.transport_bill_amount)}
                  </td>
                  <td className="px-2 py-2 bg-brand-sand/[0.05] text-right tabular-nums">{t.transport_gst_percent || 0}%</td>
                  <td className="px-2 py-2 bg-brand-sand/[0.05] font-mono text-[10px] text-brand-terracotta">{t.billing_mode === "combined" ? "—" : (t.transport_bill_number || "—")}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-brand-forest">{inr(t.profit)}</td>
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-0.5">
                      <Link to={`/trucks/${t.id}/edit`}>
                        <Button size="icon" variant="ghost" className="h-6 w-6" data-testid={`edit-acc-${t.id}`}><Pencil className="h-3 w-3" /></Button>
                      </Link>
                      {!t.material_bill_id && !t.transport_bill_id && !t.combined_bill_id && (
                        <Button size="icon" variant="ghost"
                          className="h-6 w-6 text-brand-terracotta hover:bg-brand-terracotta/10"
                          onClick={() => removeTruck(t)}
                          data-testid={`delete-acc-${t.id}`}
                          data-admin-only="true"
                          title="Delete entry"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-secondary text-[11px] sticky bottom-0 border-t-2 border-brand-ink">
              <tr>
                <td colSpan={5} className="px-3 py-2 text-right uppercase tracking-[0.12em] text-brand-muted">Totals</td>
                <td colSpan={3} className="bg-brand-forest/5"></td>
                <td className="px-2 py-2 bg-brand-forest/5"></td>
                <td className="px-2 py-2 text-right tabular-nums font-semibold bg-brand-forest/5 text-brand-muted">{inr(totals.matPurchase)}</td>
                <td className="px-2 py-2 bg-brand-forest/5"></td>
                <td className="px-2 py-2 text-right tabular-nums font-semibold bg-brand-forest/5">
                  {inr(totals.matBill)}
                  {totals.combinedBill > 0 && <div className="text-[9px] text-brand-muted font-normal mt-0.5">+ {inr(totals.combinedBill)} comb.</div>}
                </td>
                <td colSpan={2} className="bg-brand-forest/5"></td>

                <td className="bg-brand-sand/10"></td>
                <td className="bg-brand-sand/10"></td>
                <td className="px-2 py-2 text-right tabular-nums font-semibold bg-brand-sand/10 text-brand-muted">{inr(totals.trnPurchase)}</td>
                <td className="bg-brand-sand/10"></td>
                <td className="px-2 py-2 text-right tabular-nums font-semibold bg-brand-sand/10">
                  {inr(totals.trnBill)}
                  {totals.combinedBill > 0 && <div className="text-[9px] text-brand-muted font-normal mt-0.5">comb. shown</div>}
                </td>
                <td colSpan={2} className="bg-brand-sand/10"></td>

                <td className="px-3 py-2 text-right tabular-nums font-semibold text-brand-forest">{inr(totals.profit)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>

      <div className="mt-4 text-xs text-brand-muted leading-relaxed">
        <span className="font-semibold text-brand-ink">Legend:</span> Purchase Rate/Amt = what <em>you paid</em> (material from supplier · transport cost paid to transporter).
        Bill Rate/Amt = what <em>you charge</em> the party. <span className="font-semibold">Combined-mode trucks</span> (marked <span className="px-1 py-0.5 rounded-sm bg-brand-sand/40 text-[9px] font-semibold">COMB</span>) bill the party at one inclusive rate — shown in the Transport Bill column with the combined invoice number under Material Bill #. Profit = total billed − total purchase across both sides.
      </div>
    </div>
  );
}
